import { useServicesPeopleList } from './useServicesPeople.ts'
import {
  servicesKeys,
  usePlan,
  usePlanEditor,
  type PlanEdit,
  type PlanItem,
  type ServicePosition,
} from './useServices.ts'
import { templateKeys, useTemplateEditor, type TemplateEdit } from './usePlanTemplates.ts'

/**
 * A running order, whoever owns it: a plan or a plan template
 * (docs/14-TEAMS-PEOPLE-TEMPLATES.md). The order's screens — its rows, an
 * item's panel, adding songs and items — are written once against this,
 * and work on either.
 */
export type OrderRef = { kind: 'plan'; id: string } | { kind: 'template'; id: string }

/** Any change an order's screens make, on a plan or a template. */
export type OrderEdit = PlanEdit | TemplateEdit

/** What a change answers with: the order again, as the server has it now. */
type OrderData = { items: PlanItem[] } | undefined

interface OrderMutation<V> {
  mutate: (
    variables: V,
    options?: { onSuccess?: (data: OrderData) => void; onSettled?: () => void },
  ) => void
  mutateAsync: (variables: V) => Promise<OrderData>
  isPending: boolean
  error: Error | null
}

export interface AddItemBody {
  /** An item (linked to `songId`, or not) or a section (`header`). */
  kind: 'header' | 'item'
  title?: string
  songId?: string | null
  lengthSeconds?: number | null
  servicePosition?: ServicePosition
}

export interface ItemPatchBody {
  itemId: string
  title?: string
  /** Link it to a song (named after it, with its arrangement and key), or null to unlink. */
  songId?: string | null
  /** Who leads it (the song leader), on a linked item; null clears it. */
  leaderPersonId?: string | null
  key?: string | null
  lengthSeconds?: number | null
  notes?: string | null
  arrangementId?: string
  servicePosition?: ServicePosition
  sequence?: string[] | null
  categoryNotes?: Record<string, string | null>
}

export interface OrderEditor {
  ref: OrderRef
  /** The order as it is now: its items, its clock's start, its note categories. */
  order: { items: PlanItem[]; startTime: string; noteCategories: string[] } | undefined
  addItem: OrderMutation<AddItemBody>
  updateItem: OrderMutation<ItemPatchBody>
  removeItem: OrderMutation<string>
  /** Moves an item within its phase (Before, During or After). */
  move: (from: number, to: number) => void
  /** Live mode: a plan's alone. */
  goLive: {
    mutate: (itemId: string | null, options?: { onSuccess?: () => void }) => void
    isPending: boolean
  } | null
  /** Every editing mutation, by name: what a failed change's Retry sends again. */
  mutations: Partial<Record<OrderEdit, { mutate: (variables: never) => void }>>
}

/** The editor of a plan's or a template's running order. */
export function useOrderEditor(ref: OrderRef): OrderEditor {
  const plan = usePlanEditor(ref.kind === 'plan' ? ref.id : '')
  const template = useTemplateEditor(ref.kind === 'template' ? ref.id : '')
  if (ref.kind === 'plan')
    return {
      ref,
      order: plan.plan,
      addItem: plan.addItem,
      updateItem: plan.updateItem,
      removeItem: plan.removeItem,
      move: plan.move,
      goLive: plan.goLive,
      mutations: plan,
    }
  return {
    ref,
    order: template.template,
    addItem: template.addItem,
    updateItem: template.updateItem,
    removeItem: template.removeItem,
    move: template.move,
    goLive: null,
    mutations: template,
  }
}

/** Where an order's changes are filed (mutation keys): one save status reads them all. */
export const orderEditsKey = (ref: OrderRef) =>
  ref.kind === 'plan' ? servicesKeys.planEdits(ref.id) : templateKeys.edits(ref.id)

/** Someone who could lead an item: who they are, and (muted) why they are offered. */
export interface LeaderChoice {
  personId: string
  name: string
  avatarUrl: string | null
  /** "Worship Leader" for someone on the plan; their team for a search match. */
  hint: string | null
}

/**
 * Who could lead an item (the song leader): the plan's own people first —
 * whoever is scheduled on it and has not declined, each once — then, as a
 * name is typed, anyone in the church that ForServices shows the reader.
 * A template has no people of its own, so it offers the search alone.
 */
export function useLeaderChoices(ref: OrderRef, term: string) {
  const plan = usePlan(ref.kind === 'plan' ? ref.id : '')
  const typed = term.trim()
  const search = useServicesPeopleList({ q: typed, mine: false })
  const words = typed.toLowerCase()
  const onPlan = new Map<string, LeaderChoice>()
  for (const person of plan.data?.people ?? []) {
    if (person.status === 'declined' || onPlan.has(person.personId)) continue
    onPlan.set(person.personId, {
      personId: person.personId,
      name: person.name,
      avatarUrl: person.avatarUrl,
      hint: person.positionName,
    })
  }
  const scheduled = [...onPlan.values()].filter(
    (person) => !words || person.name.toLowerCase().includes(words),
  )
  const others: LeaderChoice[] =
    typed.length === 0
      ? []
      : search.people
          .filter((person) => !onPlan.has(person.id))
          .slice(0, 8)
          .map((person) => ({
            personId: person.id,
            name: person.name,
            avatarUrl: person.avatarUrl,
            hint: person.teams[0]?.name ?? null,
          }))
  return { scheduled, others, searching: typed.length > 0 && search.isFetching }
}
